import type { ReactNode } from 'react';

import { CheckCircle2, X, XCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { AcervoImportProgress } from '@/api/acervo';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface AcervoImportCardProps {
  progresso: AcervoImportProgress | null;
  onCancelar: () => void;
  onDispensar: () => void;
  /** Ao lado do botão de importar (telas largas): uma linha curta, com o resumo inteiro no título. */
  compacto?: boolean;
}

function plural(n: number, singular: string, pluralForm: string) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/** Faixa discreta de uma linha: ícone, texto e a ação à direita. */
function Faixa({
  icone,
  children,
  acao,
  barra,
  compacto,
  title,
}: {
  icone: ReactNode;
  children: ReactNode;
  acao: ReactNode;
  barra?: ReactNode;
  compacto?: boolean;
  title?: string;
}) {
  return (
    <div
      title={title}
      className={cn(
        'relative flex items-center gap-2 overflow-hidden rounded-md border bg-muted/40 text-sm',
        compacto ? 'max-w-[22rem] py-1 pl-2.5 pr-1' : 'py-1.5 pl-3 pr-1.5',
      )}
    >
      {icone}
      <div className="min-w-0 flex-1">{children}</div>
      {acao}
      {barra}
    </div>
  );
}

function BotaoFechar({ onClick, compacto }: { onClick: () => void; compacto?: boolean }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        'shrink-0 p-0 text-muted-foreground',
        compacto ? 'h-7 w-7' : 'h-11 w-11 sm:h-7 sm:w-7',
      )}
      aria-label="Fechar aviso"
    >
      <X className="h-4 w-4" />
    </Button>
  );
}

/**
 * Progresso e resumo do import do acervo (Data Lake → processos e clientes). Só cadastra: os
 * documentos de cada processo são baixados quando chega intimação ou quando os autos são abertos.
 */
export function AcervoImportCard({ progresso, onCancelar, onDispensar, compacto }: AcervoImportCardProps) {
  if (!progresso || progresso.status === 'idle') return null;

  if (progresso.status === 'PENDING' || progresso.status === 'RUNNING') {
    const total = progresso.total_estimado ?? 0;
    const feitos = progresso.processados ?? 0;
    const rodando = progresso.status === 'RUNNING' && total > 0;
    const criados = progresso.criados ?? 0;
    return (
      <Faixa
        compacto={compacto}
        icone={<Spinner size={16} className="shrink-0 text-muted-foreground" />}
        acao={
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelar}
            className={cn('shrink-0 text-muted-foreground', compacto ? 'h-7' : 'h-11 sm:h-7')}
          >
            Cancelar
          </Button>
        }
        barra={
          rodando && (
            <Progress
              value={Math.min(feitos, total)}
              max={total}
              className="absolute inset-x-0 bottom-0 h-0.5 rounded-none"
              aria-label="Progresso da importação"
            />
          )
        }
      >
        <p className={cn('text-muted-foreground', compacto ? 'truncate' : 'line-clamp-2')}>
          {progresso.status === 'PENDING'
            ? 'Importação na fila…'
            : rodando
              ? `${feitos} de ~${plural(total, 'processo analisado', 'processos analisados')}${
                  criados > 0 ? ` · ${plural(criados, 'novo', 'novos')}` : ''
                }`
              : 'Importando seus processos…'}
        </p>
      </Faixa>
    );
  }

  if (progresso.status === 'COMPLETED') {
    const criados = progresso.criados ?? 0;
    const atualizados = progresso.atualizados ?? 0;
    const encerrados = progresso.descartados_encerrados ?? 0;
    const clientes = progresso.clientes_criados ?? 0;
    const cortados = progresso.cortados_pelo_plano ?? 0;
    const partes = [
      criados > 0 ? plural(criados, 'processo novo', 'processos novos') : 'Nenhum processo novo',
      atualizados > 0 && plural(atualizados, 'atualizado', 'atualizados'),
      clientes > 0 && plural(clientes, 'cliente cadastrado', 'clientes cadastrados'),
      encerrados > 0 && plural(encerrados, 'encerrado não importado', 'encerrados não importados'),
    ].filter(Boolean);
    const resumo = `${partes.join(' · ')}.`;
    // No compacto cabe só o essencial; o resumo inteiro fica no título da faixa.
    const curto = criados > 0 ? plural(criados, 'processo novo', 'processos novos') : 'Nenhum processo novo';
    return (
      <Faixa
        compacto={compacto}
        title={compacto ? resumo : undefined}
        icone={<CheckCircle2 className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />}
        acao={<BotaoFechar onClick={onDispensar} compacto={compacto} />}
      >
        {compacto ? (
          <p className="truncate">
            {curto}
            {clientes > 0 && (
              <span className="text-muted-foreground">{` · ${plural(clientes, 'cliente', 'clientes')}`}</span>
            )}
          </p>
        ) : (
          <p className="line-clamp-2">
            <span className="font-medium">Importação concluída</span>{' '}
            <span className="text-muted-foreground">{resumo}</span>
          </p>
        )}
        {cortados > 0 && (
          <p className="text-muted-foreground">
            Seu plano chegou ao limite de processos: {plural(cortados, 'não foi importado', 'não foram importados')}.{' '}
            <Link to="/app/assinatura" className="underline">
              Ver planos
            </Link>
          </p>
        )}
      </Faixa>
    );
  }

  if (progresso.status === 'FAILED') {
    return (
      <Faixa
        icone={<XCircle className="h-4 w-4 shrink-0 text-destructive" />}
        acao={<BotaoFechar onClick={onDispensar} />}
      >
        <p className="line-clamp-2">
          <span className="font-medium">Importação não concluída</span>{' '}
          <span className="text-muted-foreground">
            {progresso.error ?? 'Não foi possível importar seus processos.'}
          </span>
        </p>
      </Faixa>
    );
  }

  if (progresso.status === 'CANCELLED') {
    return (
      <Faixa
        compacto={compacto}
        icone={<XCircle className="h-4 w-4 shrink-0 text-muted-foreground" />}
        acao={<BotaoFechar onClick={onDispensar} compacto={compacto} />}
      >
        <p className={cn('text-muted-foreground', compacto ? 'truncate' : 'line-clamp-2')}>Importação cancelada.</p>
      </Faixa>
    );
  }

  return null;
}
